import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger 
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { 
  ArrowLeft, Users, Lock, Globe, Loader2, Settings, UserPlus, Check, X,
  MoreVertical, Shield, ShieldOff, UserMinus, Crown, LogOut, Trash2, Edit2,
  MessageCircle, Send, Calendar, Megaphone, Plus, MessagesSquare, Church, Search, Trophy
} from "lucide-react";
import { 
  getGroup, deleteGroup, joinGroup, leaveGroup,
  getGroupRequests, approveGroupRequest, rejectGroupRequest,
  removeGroupMember, addGroupAdmin, removeGroupAdmin, 
  transferGroupOwnership, giveUpGroupAdmin, getGroupCategories,
  getGroupPosts, createGroupPost, likeGroupPost, deleteGroupPost,
  getGroupEvents, createGroupEvent, deleteGroupEvent,
  toggleGroupEventRsvp, resolveImageUrl, searchUsers, inviteToGroup
} from "@/lib/api";

// Import refactored components
import { 
  categoryIcons, categoryColors, GroupPost, EventCard, 
  EditGroupModal, TransferOwnershipModal, CreateEventModal 
} from "@/components/group";
import GroupChat from "@/components/group/GroupChat.jsx";
import ChallengesTab from "@/pages/ChallengesTab";

export default function GroupDetailPage({ currentUser }) {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [group, setGroup] = useState(null);
  const [requests, setRequests] = useState([]);
  const [categories, setCategories] = useState([]);
  const [posts, setPosts] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  const [createEventModalOpen, setCreateEventModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("feed");
  
  // New post state
  const [newPostContent, setNewPostContent] = useState("");
  const [isAnnouncement, setIsAnnouncement] = useState(false);
  const [postSubmitting, setPostSubmitting] = useState(false);
  
  // Invite modal state
  const [inviteModalOpen, setInviteModalOpen] = useState(false);

  useEffect(() => {
    loadGroup();
    loadCategories();
  }, [groupId]);

  useEffect(() => {
    if (group?.is_member) {
      loadPosts();
      loadEvents();
    }
  }, [group?.is_member, groupId]);

  const loadCategories = async () => {
    try {
      const res = await getGroupCategories();
      setCategories(res.data.categories || []);
    } catch {
      setCategories([]);
    }
  };

  const loadGroup = async () => {
    setLoading(true);
    try {
      const res = await getGroup(groupId);
      setGroup(res.data);
      
      if (res.data.is_admin) {
        const reqRes = await getGroupRequests(groupId);
        setRequests(reqRes.data.requests || []);
      }
    } catch {
      toast.error("Failed to load group");
      navigate("/groups");
    }
    setLoading(false);
  };

  const loadPosts = async () => {
    try {
      const res = await getGroupPosts(groupId);
      setPosts(res.data.posts || []);
    } catch {
      console.error("Failed to load posts");
    }
  };

  const loadEvents = async () => {
    try {
      const res = await getGroupEvents(groupId);
      setEvents(res.data.events || []);
    } catch {
      console.error("Failed to load events");
    }
  };

  const handleCreatePost = async (e) => {
    e.preventDefault();
    if (!newPostContent.trim()) return;

    setPostSubmitting(true);
    try {
      await createGroupPost(groupId, { content: newPostContent, is_announcement: isAnnouncement });
      toast.success(isAnnouncement ? "Announcement posted!" : "Post created!");
      setNewPostContent("");
      setIsAnnouncement(false);
      loadPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create post");
    }
    setPostSubmitting(false);
  };

  const handleLikePost = async (postId) => {
    try {
      const res = await likeGroupPost(groupId, postId);
      setPosts(posts.map(p => 
        p.post_id === postId 
          ? { ...p, user_liked: res.data.liked, like_count: res.data.like_count }
          : p
      ));
    } catch {
      toast.error("Failed to like post");
    }
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm("Are you sure you want to delete this post?")) return;
    
    try {
      await deleteGroupPost(groupId, postId);
      toast.success("Post deleted");
      setPosts(posts.filter(p => p.post_id !== postId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete post");
    }
  };

  const handleCreateEvent = async (data) => {
    try {
      await createGroupEvent(groupId, data);
      toast.success("Event created!");
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create event");
      throw err;
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm("Are you sure you want to delete this event?")) return;
    
    try {
      await deleteGroupEvent(groupId, eventId);
      toast.success("Event deleted");
      setEvents(events.filter(e => e.event_id !== eventId));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete event");
    }
  };

  const handleRsvp = async (eventId) => {
    try {
      const res = await toggleGroupEventRsvp(groupId, eventId);
      setEvents(events.map(e => 
        e.event_id === eventId 
          ? { ...e, user_rsvp: res.data.rsvp, rsvp_count: res.data.rsvp_count }
          : e
      ));
      toast.success(res.data.rsvp ? "You're going!" : "RSVP removed");
    } catch {
      toast.error("Failed to update RSVP");
    }
  };

  const handleJoin = async () => {
    setActionLoading("join");
    try {
      const res = await joinGroup(groupId);
      toast.success(res.data.message);
      loadGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to join");
    }
    setActionLoading(null);
  };

  const handleLeave = async () => {
    if (!window.confirm("Are you sure you want to leave this group?")) return;
    
    setActionLoading("leave");
    try {
      await leaveGroup(groupId);
      toast.success("Left group");
      navigate("/groups");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to leave group");
    }
    setActionLoading(null);
  };

  const handleDelete = async () => {
    if (!window.confirm("Are you sure you want to delete this group? This cannot be undone.")) return;
    
    setActionLoading("delete");
    try {
      await deleteGroup(groupId);
      toast.success("Group deleted");
      navigate("/groups");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete group");
    }
    setActionLoading(null);
  };

  const handleApproveRequest = async (requestId) => {
    setActionLoading(`approve-${requestId}`);
    try {
      await approveGroupRequest(groupId, requestId);
      toast.success("Request approved");
      loadGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to approve");
    }
    setActionLoading(null);
  };

  const handleRejectRequest = async (requestId) => {
    setActionLoading(`reject-${requestId}`);
    try {
      await rejectGroupRequest(groupId, requestId);
      toast.success("Request rejected");
      loadGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reject");
    }
    setActionLoading(null);
  };

  const handleRemoveMember = async (memberId) => {
    if (!window.confirm("Are you sure you want to remove this member?")) return;
    
    setActionLoading(`remove-${memberId}`);
    try {
      await removeGroupMember(groupId, memberId);
      toast.success("Member removed");
      loadGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove member");
    }
    setActionLoading(null);
  };

  const handleAddAdmin = async (memberId) => {
    setActionLoading(`admin-${memberId}`);
    try {
      await addGroupAdmin(groupId, memberId);
      toast.success("Admin added");
      loadGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add admin");
    }
    setActionLoading(null);
  };

  const handleRemoveAdmin = async (memberId) => {
    setActionLoading(`admin-${memberId}`);
    try {
      await removeGroupAdmin(groupId, memberId);
      toast.success("Admin removed");
      loadGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove admin");
    }
    setActionLoading(null);
  };

  const handleTransferOwnership = async (newOwnerId) => {
    try {
      await transferGroupOwnership(groupId, newOwnerId);
      toast.success("Ownership transferred");
      loadGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to transfer ownership");
      throw err;
    }
  };

  const handleGiveUpAdmin = async () => {
    if (!window.confirm("Are you sure you want to give up your admin role?")) return;
    
    setActionLoading("giveup");
    try {
      await giveUpGroupAdmin(groupId);
      toast.success("Admin role removed");
      loadGroup();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to give up admin role");
    }
    setActionLoading(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F7F2] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[var(--liturgical-primary)]" />
      </div>
    );
  }

  if (!group) return null;

  const Icon = categoryIcons[group.category] || Users;
  const color = categoryColors[group.category] || "#6B665E";
  
  const announcements = posts.filter(p => p.is_announcement);
  const regularPosts = posts.filter(p => !p.is_announcement);
  
  const now = new Date();
  const upcomingEvents = events.filter(e => new Date(e.event_date) >= now);
  const pastEvents = events.filter(e => new Date(e.event_date) < now);

  return (
    <div className="min-h-screen bg-[#F9F7F2] p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate("/groups")}
          className="mb-4 text-[#6B665E] hover:text-[#2D2A26]"
          data-testid="back-to-groups"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Groups
        </Button>

        {/* Group Header */}
        <Card className="bg-white border-[#E5E0D5] mb-6">
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
              <div 
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${color}15` }}
              >
                {group.image_url ? (
                  <img src={resolveImageUrl(group.image_url)} alt={group.name} className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <Icon className="w-10 h-10" style={{ color }} />
                )}
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="font-['Crimson_Text'] text-2xl font-bold text-[#2D2A26]">
                    {group.name}
                  </h1>
                  {group.privacy === "private" && (
                    <Badge variant="outline" className="border-[#6B665E] text-[#6B665E]">
                      <Lock className="w-3 h-3 mr-1" />
                      Private
                    </Badge>
                  )}
                  {group.privacy === "parish_only" && (
                    <Badge variant="outline" className="border-[#4169E1] text-[#4169E1]">
                      <Church className="w-3 h-3 mr-1" />
                      Parish Only
                    </Badge>
                  )}
                </div>
                
                <p className="text-[#6B665E] font-['Nunito'] mb-3">
                  {group.description}
                </p>
                
                <div className="flex items-center gap-4 flex-wrap">
                  <Badge 
                    className="font-['Nunito']"
                    style={{ backgroundColor: `${color}15`, color, borderColor: color }}
                  >
                    {group.category}
                  </Badge>
                  <span className="text-sm text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {group.member_count} {group.member_count === 1 ? "member" : "members"}
                  </span>
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex items-center gap-2 flex-shrink-0 sm:ml-auto">
                {group.is_member ? (
                  <>
                    {group.is_admin && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="icon" className="border-[#E5E0D5]" data-testid="group-settings-btn">
                            <Settings className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="bg-white border-[#E5E0D5]">
                          <DropdownMenuItem onClick={() => setInviteModalOpen(true)} className="font-['Nunito']" data-testid="invite-members-btn">
                            <UserPlus className="w-4 h-4 mr-2" />
                            Invite Members
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => setEditModalOpen(true)} className="font-['Nunito']">
                            <Edit2 className="w-4 h-4 mr-2" />
                            Edit Group
                          </DropdownMenuItem>
                          {group.is_creator && (
                            <DropdownMenuItem onClick={() => setTransferModalOpen(true)} className="font-['Nunito']">
                              <Crown className="w-4 h-4 mr-2" />
                              Transfer Ownership
                            </DropdownMenuItem>
                          )}
                          {!group.is_creator && (
                            <DropdownMenuItem onClick={handleGiveUpAdmin} className="font-['Nunito'] text-amber-600">
                              <ShieldOff className="w-4 h-4 mr-2" />
                              Give Up Admin Role
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          {group.is_creator && (
                            <DropdownMenuItem onClick={handleDelete} className="font-['Nunito'] text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete Group
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    
                    {!group.is_creator && (
                      <Button
                        variant="outline"
                        onClick={handleLeave}
                        disabled={actionLoading === "leave"}
                        className="border-[#E5E0D5] text-[#6B665E] hover:text-[var(--liturgical-primary)] hover:border-[var(--liturgical-primary)]"
                        data-testid="leave-group-btn"
                      >
                        {actionLoading === "leave" ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <>
                            <LogOut className="w-4 h-4 mr-2" />
                            Leave
                          </>
                        )}
                      </Button>
                    )}
                  </>
                ) : group.has_pending_request ? (
                  <Button disabled className="border-[var(--liturgical-secondary)] text-[var(--liturgical-secondary)]" variant="outline">
                    Request Pending
                  </Button>
                ) : (
                  <Button
                    onClick={handleJoin}
                    disabled={actionLoading === "join"}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white"
                    data-testid="join-group-btn"
                  >
                    {actionLoading === "join" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <>
                        <UserPlus className="w-4 h-4 mr-2" />
                        {group.privacy === "private" ? "Request to Join" : "Join Group"}
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Main Content Tabs */}
        {group.is_member && (
          <>
            <div className="flex gap-2 mb-4 overflow-x-auto pb-2 -mx-4 px-4">
              {[
                { key: "feed", label: "Feed", icon: MessageCircle, color: "var(--liturgical-primary)" },
                { key: "chat", label: "Chat", icon: MessagesSquare, color: "#4169E1" },
                { key: "events", label: `Events (${upcomingEvents.length})`, icon: Calendar, color: "#556B2F" },
                { key: "challenges", label: "Challenges", icon: Trophy, color: "#D4AF37" },
                { key: "announcements", label: `Announcements (${announcements.length})`, icon: Megaphone, color: "#D4AF37" },
                { key: "members", label: `Members (${group.member_count})`, icon: Users, color: "#6B665E" },
              ].map(tab => (
                <Button
                  key={tab.key}
                  variant={activeTab === tab.key ? "default" : "outline"}
                  size="sm"
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-shrink-0 text-xs sm:text-sm ${activeTab === tab.key ? '' : 'border-[#E5E0D5]'}`}
                  style={activeTab === tab.key ? { backgroundColor: tab.color } : {}}
                  data-testid={`tab-${tab.key}`}
                >
                  <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                  {tab.label}
                </Button>
              ))}
              {group.is_admin && requests.length > 0 && (
                <Button
                  variant={activeTab === "requests" ? "default" : "outline"}
                  onClick={() => setActiveTab("requests")}
                  className={activeTab === "requests" ? "bg-[#FF6347]" : "border-[#E5E0D5]"}
                  data-testid="tab-requests"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Requests ({requests.length})
                </Button>
              )}
            </div>

            {/* Feed Tab */}
            {activeTab === "feed" && (
              <FeedTab 
                posts={regularPosts}
                groupId={groupId}
                currentUser={currentUser}
                group={group}
                newPostContent={newPostContent}
                setNewPostContent={setNewPostContent}
                isAnnouncement={isAnnouncement}
                setIsAnnouncement={setIsAnnouncement}
                postSubmitting={postSubmitting}
                handleCreatePost={handleCreatePost}
                handleDeletePost={handleDeletePost}
                handleLikePost={handleLikePost}
              />
            )}

            {/* Chat Tab */}
            {activeTab === "chat" && (
              <GroupChat groupId={groupId} currentUser={currentUser} />
            )}

            {/* Events Tab */}
            {activeTab === "events" && (
              <EventsTab 
                upcomingEvents={upcomingEvents}
                pastEvents={pastEvents}
                groupId={groupId}
                group={group}
                setCreateEventModalOpen={setCreateEventModalOpen}
                handleDeleteEvent={handleDeleteEvent}
                handleRsvp={handleRsvp}
              />
            )}

            {/* Challenges Tab */}
            {activeTab === "challenges" && (
              <ChallengesTab groupId={groupId} />
            )}

            {/* Announcements Tab */}
            {activeTab === "announcements" && (
              <AnnouncementsTab
                announcements={announcements}
                groupId={groupId}
                currentUser={currentUser}
                group={group}
                newPostContent={newPostContent}
                setNewPostContent={setNewPostContent}
                setIsAnnouncement={setIsAnnouncement}
                postSubmitting={postSubmitting}
                handleCreatePost={handleCreatePost}
                handleDeletePost={handleDeletePost}
                handleLikePost={handleLikePost}
              />
            )}

            {/* Members Tab */}
            {activeTab === "members" && (
              <MembersTab 
                group={group}
                currentUser={currentUser}
                handleAddAdmin={handleAddAdmin}
                handleRemoveAdmin={handleRemoveAdmin}
                handleRemoveMember={handleRemoveMember}
              />
            )}

            {/* Requests Tab */}
            {group.is_admin && activeTab === "requests" && (
              <RequestsTab 
                requests={requests}
                actionLoading={actionLoading}
                handleApproveRequest={handleApproveRequest}
                handleRejectRequest={handleRejectRequest}
              />
            )}
          </>
        )}

        {/* Not a member message */}
        {!group.is_member && (
          <Card className="bg-white border-[#E5E0D5]">
            <CardContent className="py-12 text-center">
              <Lock className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
              <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">
                Join to see group content
              </h3>
              <p className="text-sm text-[#6B665E] font-['Nunito']">
                {group.privacy === "private" 
                  ? "Request to join this private group to see posts, events, and announcements"
                  : "Join this group to see posts, events, and announcements"}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Modals */}
        <EditGroupModal 
          isOpen={editModalOpen}
          onClose={() => setEditModalOpen(false)}
          group={group}
          categories={categories}
          onSuccess={loadGroup}
        />

        <TransferOwnershipModal 
          isOpen={transferModalOpen}
          onClose={() => setTransferModalOpen(false)}
          members={group.members_list || []}
          currentUserId={currentUser.user_id}
          onTransfer={handleTransferOwnership}
        />

        <CreateEventModal
          isOpen={createEventModalOpen}
          onClose={() => setCreateEventModalOpen(false)}
          onSuccess={handleCreateEvent}
        />
        
        {/* Invite Members Modal */}
        <InviteMembersModal
          isOpen={inviteModalOpen}
          onClose={() => setInviteModalOpen(false)}
          groupId={groupId}
          currentUser={currentUser}
          existingMembers={group.members || []}
          onSuccess={loadGroup}
        />
      </div>
    </div>
  );
}

// Invite Members Modal - for post-creation invitations
function InviteMembersModal({ isOpen, onClose, groupId, currentUser, existingMembers, onSuccess }) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [inviteMessage, setInviteMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery("");
      setSearchResults([]);
      setSelectedUsers([]);
      setInviteMessage("");
    }
  }, [isOpen]);

  useEffect(() => {
    const debounce = setTimeout(() => {
      if (searchQuery.trim()) {
        handleSearch();
      } else {
        setSearchResults([]);
      }
    }, 300);
    return () => clearTimeout(debounce);
  }, [searchQuery]);

  const handleSearch = async () => {
    setSearchLoading(true);
    try {
      const res = await searchUsers({ search: searchQuery, limit: 20 });
      const filtered = (res.data.users || []).filter(
        u => u.user_id !== currentUser?.user_id &&
             !existingMembers.includes(u.user_id) &&
             !selectedUsers.find(s => s.user_id === u.user_id)
      );
      setSearchResults(filtered);
    } catch {
      setSearchResults([]);
    }
    setSearchLoading(false);
  };

  const handleSendInvites = async () => {
    if (selectedUsers.length === 0) return;
    setSending(true);
    try {
      const res = await inviteToGroup(groupId, selectedUsers.map(u => u.user_id), inviteMessage || null);
      toast.success(`Invitations sent to ${res.data.invited} member(s)!`);
      if (res.data.already_members > 0) toast.info(`${res.data.already_members} already member(s)`);
      if (res.data.already_invited > 0) toast.info(`${res.data.already_invited} already invited`);
      onSuccess?.();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send invitations");
    }
    setSending(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-[#E5E0D5] max-w-md max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Crimson_Text'] text-xl text-[#2D2A26]">
            Invite Members
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6B665E]" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search members to invite..."
              className="pl-9 border-[#E5E0D5] font-['Nunito']"
              data-testid="group-invite-search"
            />
            {searchLoading && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-[#6B665E]" />}
          </div>

          {searchResults.length > 0 && (
            <div className="border border-[#E5E0D5] rounded-lg max-h-40 overflow-y-auto">
              {searchResults.map(user => (
                <button
                  key={user.user_id}
                  onClick={() => {
                    setSelectedUsers([...selectedUsers, user]);
                    setSearchResults(searchResults.filter(u => u.user_id !== user.user_id));
                    setSearchQuery("");
                  }}
                  className="w-full flex items-center gap-3 p-2 hover:bg-[#F9F7F2] transition-colors text-left"
                  data-testid={`invite-result-${user.user_id}`}
                >
                  <Avatar className="h-8 w-8">
                    <AvatarImage src={resolveImageUrl(user.picture)} />
                    <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-xs">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm font-medium text-[#2D2A26] font-['Nunito'] flex-1 truncate">{user.name}</span>
                  <Plus className="w-4 h-4 text-[var(--liturgical-primary)]" />
                </button>
              ))}
            </div>
          )}

          {selectedUsers.length > 0 && (
            <div>
              <p className="text-xs text-[#6B665E] font-['Nunito'] mb-2">{selectedUsers.length} member(s) selected</p>
              <div className="flex flex-wrap gap-2">
                {selectedUsers.map(user => (
                  <Badge key={user.user_id} variant="secondary" className="bg-[var(--liturgical-primary)]/10 text-[var(--liturgical-primary)] pl-1 pr-1">
                    <Avatar className="h-5 w-5 mr-1">
                      <AvatarImage src={resolveImageUrl(user.picture)} />
                      <AvatarFallback className="bg-[var(--liturgical-primary)] text-white text-[10px]">{user.name?.[0]?.toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <span className="text-xs font-['Nunito']">{user.name}</span>
                    <button onClick={() => setSelectedUsers(selectedUsers.filter(u => u.user_id !== user.user_id))} className="ml-1 hover:bg-[var(--liturgical-primary)]/20 rounded-full p-0.5">
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <Input
                value={inviteMessage}
                onChange={(e) => setInviteMessage(e.target.value)}
                placeholder="Add a personal message (optional)..."
                className="border-[#E5E0D5] font-['Nunito'] text-sm mt-3"
                data-testid="group-invite-message"
              />
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-[#E5E0D5]">Cancel</Button>
          <Button onClick={handleSendInvites} disabled={sending || selectedUsers.length === 0} className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white" data-testid="send-invites-btn">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <UserPlus className="w-4 h-4 mr-2" />}
            Send Invites ({selectedUsers.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Tab Components
function FeedTab({ posts, groupId, currentUser, group, newPostContent, setNewPostContent, isAnnouncement, setIsAnnouncement, postSubmitting, handleCreatePost, handleDeletePost, handleLikePost }) {
  return (
    <div className="space-y-4">
      <Card className="bg-white border-[#E5E0D5]">
        <CardContent className="p-4">
          <form onSubmit={handleCreatePost}>
            <div className="flex gap-3">
              <Avatar className="h-10 w-10">
                <AvatarImage src={resolveImageUrl(currentUser?.picture)} />
                <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">
                  {currentUser?.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <Textarea
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  placeholder="Share something with the group..."
                  className="border-[#E5E0D5] min-h-[80px] mb-3"
                  data-testid="new-post-input"
                />
                <div className="flex items-center justify-between">
                  {group.is_admin && (
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={isAnnouncement}
                        onChange={(e) => setIsAnnouncement(e.target.checked)}
                        className="rounded border-[#E5E0D5]"
                      />
                      <span className="text-sm text-[#6B665E] font-['Nunito'] flex items-center gap-1">
                        <Megaphone className="w-4 h-4" />
                        Post as Announcement
                      </span>
                    </label>
                  )}
                  <Button 
                    type="submit" 
                    disabled={postSubmitting || !newPostContent.trim()}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90 ml-auto"
                    data-testid="submit-post-btn"
                  >
                    {postSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                    Post
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </CardContent>
      </Card>

      {posts.length === 0 ? (
        <Card className="bg-white border-[#E5E0D5]">
          <CardContent className="py-12 text-center">
            <MessageCircle className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
            <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">
              No posts yet
            </h3>
            <p className="text-sm text-[#6B665E] font-['Nunito']">
              Be the first to share something with the group!
            </p>
          </CardContent>
        </Card>
      ) : (
        posts.map(post => (
          <GroupPost
            key={post.post_id}
            post={post}
            groupId={groupId}
            currentUserId={currentUser?.user_id}
            isAdmin={group.is_admin}
            onDelete={handleDeletePost}
            onLike={handleLikePost}
          />
        ))
      )}
    </div>
  );
}

function EventsTab({ upcomingEvents, pastEvents, groupId, group, setCreateEventModalOpen, handleDeleteEvent, handleRsvp }) {
  return (
    <div className="space-y-4">
      {group.is_admin && (
        <Button
          onClick={() => setCreateEventModalOpen(true)}
          className="bg-[var(--liturgical-primary)] hover:brightness-90 w-full"
          data-testid="create-event-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Create Event
        </Button>
      )}

      {upcomingEvents.length > 0 && (
        <div>
          <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-3">Upcoming Events</h3>
          <div className="space-y-3">
            {upcomingEvents.map(event => (
              <EventCard key={event.event_id} event={event} groupId={groupId} isAdmin={group.is_admin} onDelete={handleDeleteEvent} onRsvp={handleRsvp} />
            ))}
          </div>
        </div>
      )}

      {pastEvents.length > 0 && (
        <div>
          <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#6B665E] mb-3 mt-6">Past Events</h3>
          <div className="space-y-3">
            {pastEvents.map(event => (
              <EventCard key={event.event_id} event={event} groupId={groupId} isAdmin={group.is_admin} onDelete={handleDeleteEvent} onRsvp={handleRsvp} />
            ))}
          </div>
        </div>
      )}

      {upcomingEvents.length === 0 && pastEvents.length === 0 && (
        <Card className="bg-white border-[#E5E0D5]">
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
            <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">No events yet</h3>
            <p className="text-sm text-[#6B665E] font-['Nunito']">
              {group.is_admin ? "Create an event to get started!" : "Check back later for upcoming events."}
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function AnnouncementsTab({ announcements, groupId, currentUser, group, newPostContent, setNewPostContent, setIsAnnouncement, postSubmitting, handleCreatePost, handleDeletePost, handleLikePost }) {
  return (
    <div className="space-y-4">
      {group.is_admin && (
        <Card className="bg-white border-[#E5E0D5]">
          <CardContent className="p-4">
            <form onSubmit={(e) => { setIsAnnouncement(true); handleCreatePost(e); }}>
              <div className="flex gap-3">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={resolveImageUrl(currentUser?.picture)} />
                  <AvatarFallback className="bg-[var(--liturgical-secondary)] text-white">
                    {currentUser?.name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1">
                  <Textarea
                    value={newPostContent}
                    onChange={(e) => setNewPostContent(e.target.value)}
                    placeholder="Write an announcement..."
                    className="border-[#E5E0D5] min-h-[80px] mb-3"
                    data-testid="new-announcement-input"
                  />
                  <Button 
                    type="submit" 
                    disabled={postSubmitting || !newPostContent.trim()}
                    className="bg-[var(--liturgical-secondary)] hover:brightness-90"
                    onClick={() => setIsAnnouncement(true)}
                    data-testid="submit-announcement-btn"
                  >
                    {postSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Megaphone className="w-4 h-4 mr-2" />}
                    Post Announcement
                  </Button>
                </div>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {announcements.length === 0 ? (
        <Card className="bg-white border-[#E5E0D5]">
          <CardContent className="py-12 text-center">
            <Megaphone className="w-12 h-12 text-[#E5E0D5] mx-auto mb-4" />
            <h3 className="font-['Crimson_Text'] text-lg font-semibold text-[#2D2A26] mb-2">No announcements</h3>
            <p className="text-sm text-[#6B665E] font-['Nunito']">
              {group.is_admin ? "Post an announcement to inform group members." : "No announcements from admins yet."}
            </p>
          </CardContent>
        </Card>
      ) : (
        announcements.map(post => (
          <GroupPost key={post.post_id} post={post} groupId={groupId} currentUserId={currentUser?.user_id} isAdmin={group.is_admin} onDelete={handleDeletePost} onLike={handleLikePost} />
        ))
      )}
    </div>
  );
}

function MembersTab({ group, currentUser, handleAddAdmin, handleRemoveAdmin, handleRemoveMember }) {
  return (
    <Card className="bg-white border-[#E5E0D5]">
      <CardHeader>
        <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26]">Members</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {group.members_list?.map(member => (
            <div key={member.user_id} className="flex items-center gap-3 p-3 bg-[#F9F7F2] rounded-lg">
              <Avatar className="h-10 w-10">
                <AvatarImage src={resolveImageUrl(member.picture)} />
                <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">
                  {member.name?.[0]?.toUpperCase()}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-['Nunito'] font-medium text-[#2D2A26]">{member.name}</span>
                  {member.is_creator && (
                    <Badge className="bg-[var(--liturgical-secondary)] text-white text-[10px]">
                      <Crown className="w-3 h-3 mr-1" />Creator
                    </Badge>
                  )}
                  {member.is_admin && !member.is_creator && (
                    <Badge className="bg-[var(--liturgical-primary)] text-white text-[10px]">
                      <Shield className="w-3 h-3 mr-1" />Admin
                    </Badge>
                  )}
                </div>
              </div>
              
              {group.is_admin && member.user_id !== currentUser.user_id && !member.is_creator && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-8 w-8">
                      <MoreVertical className="w-4 h-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-white border-[#E5E0D5]">
                    {member.is_admin ? (
                      <DropdownMenuItem onClick={() => handleRemoveAdmin(member.user_id)} className="font-['Nunito']">
                        <ShieldOff className="w-4 h-4 mr-2" />Remove Admin
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onClick={() => handleAddAdmin(member.user_id)} className="font-['Nunito']">
                        <Shield className="w-4 h-4 mr-2" />Make Admin
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => handleRemoveMember(member.user_id)} className="font-['Nunito'] text-red-600">
                      <UserMinus className="w-4 h-4 mr-2" />Remove from Group
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function RequestsTab({ requests, actionLoading, handleApproveRequest, handleRejectRequest }) {
  return (
    <Card className="bg-white border-[#E5E0D5]">
      <CardHeader>
        <CardTitle className="font-['Crimson_Text'] text-lg text-[#2D2A26]">Join Requests</CardTitle>
      </CardHeader>
      <CardContent>
        {requests.length === 0 ? (
          <p className="text-center text-[#6B665E] font-['Nunito'] py-4">No pending requests</p>
        ) : (
          <div className="space-y-3">
            {requests.map(request => (
              <div key={request.request_id} className="flex items-center gap-3 p-3 bg-[#F9F7F2] rounded-lg">
                <Avatar className="h-10 w-10">
                  <AvatarImage src={resolveImageUrl(request.user_picture)} />
                  <AvatarFallback className="bg-[var(--liturgical-primary)] text-white">
                    {request.user_name?.[0]?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <span className="font-['Nunito'] font-medium text-[#2D2A26]">{request.user_name}</span>
                  <p className="text-xs text-[#6B665E] font-['Nunito']">
                    Requested {new Date(request.created_at).toLocaleDateString()}
                  </p>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleApproveRequest(request.request_id)}
                    disabled={actionLoading === `approve-${request.request_id}`}
                    className="bg-[var(--liturgical-primary)] hover:brightness-90 text-white"
                  >
                    {actionLoading === `approve-${request.request_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRejectRequest(request.request_id)}
                    disabled={actionLoading === `reject-${request.request_id}`}
                    className="border-[#E5E0D5] text-[var(--liturgical-primary)] hover:bg-[var(--liturgical-primary)] hover:text-white"
                  >
                    {actionLoading === `reject-${request.request_id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
